/**
 * NEXUMA — Permission Service
 *
 * Core logic for resolving, checking, and managing permissions.
 * This is a pure service (no React deps) — safe to use anywhere.
 *
 * Architecture:
 *   1. Reads the current user from localStorage (same pattern as globals.js)
 *   2. Resolves raw roles → canonical role keys (via rolesConfig aliases)
 *   3. Merges role permissions + any per-user overrides
 *   4. Exposes hasPermission, hasAnyPermission, hasAllPermissions, scope helpers
 *
 * Per-user overrides format (stored in user.data.permission_overrides):
 *   {
 *     add:    ['employees.view.company', ...],
 *     remove: ['payroll.manage', ...],
 *   }
 */

import { getPermissionsForRoles, resolveRoleKey, ROLES_CONFIG } from '../config/permissions/rolesConfig';
import { SCOPES } from '../config/permissions/permissionsConfig';
import { getAuthToken, getLogedInUser } from './authSession';

const STORAGE_KEY = 'logedInUser';
const API_URL = process.env.REACT_APP_API_URL || '';

// ─── Effective permissions cache ─────────────────────────────────────────────
// The backend computes the REAL permission set (base/custom role + position +
// overrides) at /api/me/permissions. Once fetched we gate the UI on that exact
// set, so menus/buttons match what the backend enforces. Until it loads we fall
// back to the static rolesConfig resolution (safe default, never more than the
// role grants).
let _effectiveCache  = null;   // { wildcard: boolean, permissions: string[] }
let _effectiveUserId = null;   // whose permissions the cache holds
let _inFlight        = null;   // dedup concurrent fetches

/** Fetch & cache the current user's real effective permissions (deduped). */
export async function refreshEffectivePermissions() {
  if (_inFlight) return _inFlight;
  _inFlight = (async () => {
    try {
      const token = getAuthToken();
      if (!token) return null;
      const res  = await fetch(`${API_URL}/api/me/permissions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (json?.success && json.data) {
        _effectiveCache  = { wildcard: !!json.data.wildcard, permissions: json.data.permissions || [] };
        _effectiveUserId = getLogedInUser()?.data?.id ?? null;
      }
    } catch { /* keep static fallback */ }
    finally { _inFlight = null; }
    return _effectiveCache;
  })();
  return _inFlight;
}

/** Drop the cache (call on logout / user switch). */
export function clearEffectivePermissions() {
  _effectiveCache = null;
  _effectiveUserId = null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Scope hierarchy: a user with 'company' scope can also see 'department' items
// ─────────────────────────────────────────────────────────────────────────────
const SCOPE_HIERARCHY = {
  [SCOPES.ALL]:        5,
  [SCOPES.COMPANY]:    4,
  [SCOPES.DEPARTMENT]: 3,
  [SCOPES.TEAM]:       2,
  [SCOPES.OWN]:        1,
};

/**
 * Parse and return the logedInUser object from localStorage.
 */
function _readUser() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed || null;
  } catch {
    return null;
  }
}

/**
 * Extract the raw role strings from the user object.
 */
function _extractRawRoles(user) {
  if (!user) return [];
  const roles = user?.data?.roles || user?.roles || [];
  return roles
    .map((r) => (r?.name || r?.role_name || r || '').toString())
    .filter(Boolean);
}

/**
 * Build the full resolved permission set for a user.
 * Merges role permissions + per-user overrides.
 */
function _resolvePermissions(user) {
  const rawRoles = _extractRawRoles(user);
  const basePerms = getPermissionsForRoles(rawRoles);

  // Per-user overrides (if backend provides them)
  const overrides = user?.data?.permission_overrides || {};
  const add    = Array.isArray(overrides.add)    ? overrides.add    : [];
  const remove = Array.isArray(overrides.remove) ? overrides.remove : [];

  const merged = new Set([...basePerms, ...add]);
  remove.forEach((perm) => merged.delete(perm));

  return Array.from(merged);
}

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────

const permissionService = {
  /**
   * Get the full resolved permissions array for the current user.
   */
  getPermissions(user = null) {
    const u = user || _readUser();
    // Prefer the backend's real effective permissions for the CURRENT user.
    const uid = u?.data?.id ?? u?.id ?? null;
    if (_effectiveCache && (user == null || uid === _effectiveUserId)) {
      return _effectiveCache.wildcard ? ['*'] : _effectiveCache.permissions;
    }
    return _resolvePermissions(u);
  },

  /**
   * Get the resolved role keys for the current user.
   */
  getRoleKeys(user = null) {
    const u = user || _readUser();
    const rawRoles = _extractRawRoles(u);
    return rawRoles.map(resolveRoleKey).filter(Boolean);
  },

  /**
   * Get the highest-priority role config for the current user.
   */
  getPrimaryRole(user = null) {
    const keys = permissionService.getRoleKeys(user);
    if (!keys.length) return null;
    return keys
      .map((k) => ROLES_CONFIG[k])
      .filter(Boolean)
      .sort((a, b) => b.priority - a.priority)[0] || null;
  },

  /**
   * Check if the user has a specific permission.
   * Also handles scope-aware checking:
   *   hasPermission('employees.view.department') is true if user has 'employees.view.company'
   *   because company scope ⊇ department scope.
   */
  hasPermission(permissionKey, user = null) {
    if (!permissionKey) return false;
    const perms = permissionService.getPermissions(user);

    // Wildcard (super admin) grants everything.
    if (perms.includes('*')) return true;
    // Direct match
    if (perms.includes(permissionKey)) return true;

    // Scope-elevation check: employees.view.department → also check .company, .all
    const parts = permissionKey.split('.');
    if (parts.length === 3) {
      const [module, action, scope] = parts;
      const requestedLevel = SCOPE_HIERARCHY[scope] || 0;
      // Check if user has same module.action at a higher scope
      return perms.some((perm) => {
        const [m, a, s] = perm.split('.');
        return (
          m === module &&
          a === action &&
          s &&
          (SCOPE_HIERARCHY[s] || 0) > requestedLevel
        );
      });
    }

    return false;
  },

  /**
   * Returns true if the user has AT LEAST ONE of the given permissions.
   */
  hasAnyPermission(permissionKeys = [], user = null) {
    return permissionKeys.some((key) =>
      permissionService.hasPermission(key, user)
    );
  },

  /**
   * Returns true if the user has ALL of the given permissions.
   */
  hasAllPermissions(permissionKeys = [], user = null) {
    return permissionKeys.every((key) =>
      permissionService.hasPermission(key, user)
    );
  },

  /**
   * Check if the user has a specific role.
   */
  hasRole(roleKey, user = null) {
    return permissionService.getRoleKeys(user).includes(roleKey);
  },

  /**
   * Check if the user has any of the given roles.
   */
  hasAnyRole(roleKeys = [], user = null) {
    const userRoles = permissionService.getRoleKeys(user);
    return roleKeys.some((k) => userRoles.includes(k));
  },

  /**
   * Get the highest scope the user has for a given module.action.
   * e.g. getMaxScope('employees', 'view') → 'company'
   */
  getMaxScope(module, action, user = null) {
    const perms = permissionService.getPermissions(user);
    let maxLevel = 0;
    let maxScope = null;
    perms.forEach((perm) => {
      const [m, a, s] = perm.split('.');
      if (m === module && a === action && s) {
        const level = SCOPE_HIERARCHY[s] || 0;
        if (level > maxLevel) {
          maxLevel = level;
          maxScope = s;
        }
      }
    });
    return maxScope;
  },

  /**
   * Filter sidebar config items to only those the user can see.
   * Works recursively on children.
   */
  getVisibleMenuItems(sidebarConfig, user = null) {
    return sidebarConfig.reduce((acc, item) => {
      const canSee = _itemVisible(item, user);
      if (!canSee) return acc;

      if (item.children) {
        const visibleChildren = permissionService.getVisibleMenuItems(item.children, user);
        if (visibleChildren.length === 0 && item.section) return acc;
        acc.push({ ...item, children: visibleChildren });
      } else {
        acc.push(item);
      }
      return acc;
    }, []);
  },

  /**
   * Filter dashboard widgets to only those the user can see.
   * Returns sorted by priority ascending.
   */
  getVisibleWidgets(widgetsConfig, user = null) {
    return widgetsConfig
      .filter((w) => _itemVisible(w, user))
      .sort((a, b) => (a.priority || 99) - (b.priority || 99));
  },

  /**
   * Check if the current user is a "worker-only" (non-admin) user.
   * Mirrors the logic in Dashboard.js for backward compatibility.
   */
  isWorkerOnly(user = null) {
    const u = user || _readUser();
    const rawRoles = _extractRawRoles(u);
    const roleNames = rawRoles.map((r) => r.toLowerCase().trim());
    const hasAdminRole = roleNames.some(
      (r) => r === 'admin' || r === 'menaxher' || r === 'manager' ||
             resolveRoleKey(r) === 'company_admin' ||
             resolveRoleKey(r) === 'hr_manager' ||
             resolveRoleKey(r) === 'payroll_manager' ||
             resolveRoleKey(r) === 'team_lead' ||
             resolveRoleKey(r) === 'recruiter' ||
             resolveRoleKey(r) === 'super_admin' ||
             resolveRoleKey(r) === 'auditor'
    );
    return rawRoles.length > 0 && !hasAdminRole;
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Private helpers
// ─────────────────────────────────────────────────────────────────────────────

function _itemVisible(item, user) {
  if (item.requiresAll?.length) {
    return permissionService.hasAllPermissions(item.requiresAll, user);
  }
  if (item.requiresAny?.length) {
    return permissionService.hasAnyPermission(item.requiresAny, user);
  }
  // No permission requirement → always visible
  return true;
}

export default permissionService;
